import { frameId } from "../assets/manifest.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { CompileContext } from "../compile/context.ts";
import { COLLISION_CELLS_PER_TILE, type JsonValue, type LayerDocument, type MapDocument, type TileRef } from "../document/types.ts";
import { foldZones, type FoldedZones } from "../document/zones.ts";
import type { ValidationIssue, ZoneTypeDefinition } from "./adapter.ts";
import { CELLS_REACH, type FieldAddress } from "./field-kinds.ts";
import type { ReferenceThing, ReferenceTypes } from "./references.ts";
import type { JsonSchema } from "./schema.ts";

/**
 * Cells that follow a zone (ADR-021's amendment of 2026-10-02, decisions 1 to 5). A cells field, or a row of a
 * list of cells, may hold `{ zone: "<type id>" }` in place of its pairs: the area is the 4-connected region of
 * that type on the map's folded zones holding the cell nearest the centre of the thing's own box, among that
 * type's cells inside the thing's reach window (its box grown by `CELLS_REACH` tiles on every side), clipped to
 * the window. `lowerCellSources` turns every such value into the pairs it resolves to, so the compile, the
 * store's validation and the game's own checks read plain cells; `cellSourceIssues` names what the Issues list
 * warns of. Pure: nothing is kept between calls.
 */

/**
 * A frame's size in pixels, or undefined when it is not known; a thing whose frame is unknown is one tile.
 */
export type FrameBoxOf = (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;

/**
 * A compile context's frame sizes as `lowerCellSources` reads them, through `frameSizeOf`, so the editor and the
 * compile size a prop's box alike.
 *
 * @param context The compile context.
 * @returns The reader.
 */
export function contextFrameSize(context: Pick<CompileContext, "collision" | "frameSizes">): FrameBoxOf {
  return (frame) => frameSizeOf(context, frameId(frame.tileset, null, frame.tile));
}

/**
 * One followed zone of a map: whose field, which zone, how many cells it resolved to and how many the reach
 * window left out.
 */
export interface FollowedZone {
  readonly owner: ReferenceThing;
  readonly address: FieldAddress;
  readonly zone: string;
  readonly cells: number;
  readonly clipped: number;
}

/**
 * A map with every followed zone lowered to cells, and what each resolved to.
 */
export interface LoweredCells {
  readonly map: MapDocument;
  readonly followed: readonly FollowedZone[];
}

/**
 * Where a thing's cells are counted from and its own box, in collision cells of the map: the anchor (the
 * bottom-left corner of the tile its point names, floored to the grid as the map server reads it) and the box's
 * size in columns and rows up. A cell `[column, rowsUp]` of the thing is the map's cell `(ax + column,
 * ay - 1 - rowsUp)`.
 */
export interface ThingBox {
  readonly ax: number;
  readonly ay: number;
  readonly columns: number;
  readonly rows: number;
}

/**
 * A thing's reach window in the map's collision cells, inclusive: its own box grown by `CELLS_REACH` tiles on
 * every side and clipped to the map. Empty (`left > right` or `top > bottom`) when the box lies off the map.
 */
export interface ReachWindow {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * A thing's box: a marker's tile, or a prop's frame (one tile when the frame's size is unknown).
 *
 * @param point The thing's point in tiles.
 * @param size A prop frame's size in pixels, or undefined for a marker's tile.
 * @param tileSize Tile size in pixels.
 * @returns The box.
 */
export function thingBox(point: { readonly x: number; readonly y: number }, size: { readonly w: number; readonly h: number } | undefined, tileSize: number): ThingBox {
  const n = COLLISION_CELLS_PER_TILE;
  const ax = Math.floor(point.x * n);
  const ay = Math.floor((point.y + 1) * n);
  return size === undefined ? { ax, ay, columns: n, rows: n } : { ax, ay, columns: Math.max(1, Math.ceil((size.w / tileSize) * n)), rows: Math.max(1, Math.ceil((size.h / tileSize) * n)) };
}

/**
 * A box's reach window, clipped to the map's collision grid.
 *
 * @param box The thing's box.
 * @param width The grid's width in cells.
 * @param height The grid's height in cells.
 * @returns The window.
 */
export function reachWindow(box: ThingBox, width: number, height: number): ReachWindow {
  const reach = CELLS_REACH * COLLISION_CELLS_PER_TILE;
  return { left: Math.max(0, box.ax - reach), top: Math.max(0, box.ay - box.rows - reach), right: Math.min(width - 1, box.ax + box.columns - 1 + reach), bottom: Math.min(height - 1, box.ay - 1 + reach) };
}

/**
 * The box of a thing on a map.
 *
 * @param map The map.
 * @param owner The thing.
 * @param tileSize Tile size in pixels.
 * @param frameSize A prop frame's size in pixels.
 * @returns The box, or null when the thing is not on the map.
 */
export function thingBoxOn(map: MapDocument, owner: ReferenceThing, tileSize: number, frameSize: FrameBoxOf): ThingBox | null {
  const layer = map.layers.find((l) => l.id === owner.layerId);
  if (owner.kind === "prop" && layer?.kind === "prop") {
    const prop = layer.props.find((p) => p.id === owner.propId);
    return prop === undefined ? null : thingBox(prop, frameSize(prop.frame) ?? { w: tileSize, h: tileSize }, tileSize);
  }
  if (owner.kind === "marker" && layer?.kind === "entity") {
    const entity = layer.entities.find((e) => e.id === owner.entityId);
    return entity === undefined ? null : thingBox(entity, undefined, tileSize);
  }
  return null;
}

/**
 * The value a field holds while it follows a zone.
 *
 * @param type The zone type's id.
 * @returns The value.
 */
export function zoneCellSource(type: string): { readonly zone: string } {
  return { zone: type };
}

/**
 * The zone type a cells value follows.
 *
 * @param value A cells field's value or a row of a list of cells.
 * @returns The type's id, or null when the value is not exactly `{ zone: <non-empty string> }`.
 */
export function cellSourceOf(value: JsonValue | undefined): string | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as { readonly [key: string]: JsonValue };
  const keys = Object.keys(record);
  const zone = record["zone"];
  return keys.length === 1 && typeof zone === "string" && zone !== "" ? zone : null;
}

/**
 * Lowers every followed zone of a map to the cells it resolves to: a field that resolves to nothing holds no
 * value, a row an empty area. The folded zones are read once, and only when some field follows a zone.
 *
 * @param map The map.
 * @param tileSize Tile size in pixels.
 * @param frameSize A prop frame's size in pixels.
 * @returns The lowered map (the same object when nothing follows a zone) and what each followed zone resolved to,
 *   in document order.
 */
export function lowerCellSources(map: MapDocument, tileSize: number, frameSize: FrameBoxOf): LoweredCells {
  if (!map.layers.some(followsAnyZone)) {
    return { map, followed: [] };
  }
  const resolver = new ZoneResolver(foldZones(map), map.width * COLLISION_CELLS_PER_TILE, map.height * COLLISION_CELLS_PER_TILE);
  const followed: FollowedZone[] = [];
  const layers = map.layers.map((layer): LayerDocument => {
    if (layer.kind === "entity") {
      return { ...layer, entities: layer.entities.map((entity) => ({ ...entity, fields: lowerFields(entity.fields, thingBox(entity, undefined, tileSize), resolver, { kind: "marker", layerId: layer.id, entityId: entity.id }, followed) })) };
    }
    if (layer.kind === "prop") {
      return {
        ...layer,
        props: layer.props.map((prop) => {
          const behaviour = prop.behaviour;
          if (behaviour === undefined) {
            return prop;
          }
          const box = thingBox(prop, frameSize(prop.frame) ?? { w: tileSize, h: tileSize }, tileSize);
          return { ...prop, behaviour: { ...behaviour, fields: lowerFields(behaviour.fields, box, resolver, { kind: "prop", layerId: layer.id, propId: prop.id }, followed) } };
        }),
      };
    }
    return layer;
  });
  return { map: { ...map, layers }, followed };
}

/**
 * The warnings a map's followed zones raise, in the form `referenceIssues` uses: a zone type the game does not
 * register, a zone with no region in reach, and a region the reach window clipped.
 *
 * @param map The map.
 * @param followed What its followed zones resolved to, as `lowerCellSources` reports it.
 * @param types The registered types, for the things' and fields' names.
 * @param zoneTypes The registered zone types, for their labels.
 * @returns The issues, in the order of `followed`.
 */
export function cellSourceIssues(map: MapDocument, followed: readonly FollowedZone[], types: ReferenceTypes, zoneTypes: readonly ZoneTypeDefinition[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const entry of followed) {
    const zone = zoneTypes.find((t) => t.id === entry.zone);
    const lead = `${thingWords(map, entry.owner, types)} whose ${fieldTitle(map, entry.owner, entry.address, types)}`;
    let message: string | null = null;
    const kind = zone === undefined ? "cells.zone-unregistered" : entry.cells === 0 ? "cells.zone-none" : "cells.zone-clipped";
    if (zone === undefined) {
      message = `${lead} follows zone type ${entry.zone}, which the game does not register; choose another or paint the area by hand`;
    } else if (entry.cells === 0) {
      message = `${lead} follows the ${zone.label} zone, but none is painted within ${CELLS_REACH} tiles of it; paint one there or paint the area by hand`;
    } else if (entry.clipped > 0) {
      message = `${lead} follows the ${zone.label} zone, which reaches past ${CELLS_REACH} tiles from it; ${entry.clipped} of its cells are left out`;
    }
    if (message !== null) {
      issues.push(entry.owner.kind === "prop" ? { mapId: map.id, kind, ignorable: true, layerId: entry.owner.layerId, propId: entry.owner.propId, severity: "warning", message } : { mapId: map.id, kind, ignorable: true, layerId: entry.owner.layerId, entityId: entry.owner.entityId, severity: "warning", message });
    }
  }
  return issues;
}

/**
 * Resolves followed zones against one map's folded zones. The folded grid's 4-connected regions are labelled once,
 * on the first resolve, each label with its size, so every resolve scans only its own reach window: it finds the
 * nearest cell of the type there, keeps the window's cells carrying that cell's label, and counts the rest of the
 * region as clipped, however many things follow a zone and however large the region.
 */
class ZoneResolver {
  private labels: Int32Array | null = null;
  private readonly sizes: number[] = [];

  /**
   * @param folded The map's folded zones.
   * @param width The collision grid's width.
   * @param height The collision grid's height.
   */
  public constructor(
    private readonly folded: FoldedZones,
    private readonly width: number,
    private readonly height: number,
  ) {}

  /**
   * The cells a zone type gives a thing: the region holding the nearest cell of the type in the thing's reach
   * window, as `[column, rowsUp]` pairs from its anchor sorted by row then column, and how many of the region's
   * cells lie outside the window.
   *
   * @param type The zone type's id.
   * @param box The thing's anchor and box.
   * @returns The pairs and the clipped count.
   */
  public resolve(type: string, box: ThingBox): { readonly pairs: [number, number][]; readonly clipped: number } {
    const value = this.folded.types.indexOf(type) + 1;
    const { left, right, top, bottom } = reachWindow(box, this.width, this.height);
    if (value === 0 || left > right || top > bottom) {
      return { pairs: [], clipped: 0 };
    }
    const cx = box.ax + box.columns / 2;
    const cy = box.ay - box.rows / 2;
    let nearest = -1;
    let best = Infinity;
    for (let y = top; y <= bottom; y += 1) {
      for (let x = left; x <= right; x += 1) {
        const index = y * this.width + x;
        if (this.folded.cells[index] !== value) {
          continue;
        }
        const distance = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2;
        if (distance < best) {
          best = distance;
          nearest = index;
        }
      }
    }
    if (nearest < 0) {
      return { pairs: [], clipped: 0 };
    }
    const labels = this.labelled();
    const label = labels[nearest] as number;
    const pairs: [number, number][] = [];
    for (let y = bottom; y >= top; y -= 1) {
      for (let x = left; x <= right; x += 1) {
        if (labels[y * this.width + x] === label) {
          pairs.push([x - box.ax, box.ay - 1 - y]);
        }
      }
    }
    return { pairs, clipped: (this.sizes[label] as number) - pairs.length };
  }

  /**
   * The folded grid's region labels, computed on first use: each painted cell's 4-connected region of one type, -1
   * for an unpainted cell, with each region's size kept in `sizes`.
   *
   * @returns The labels, one per cell.
   */
  private labelled(): Int32Array {
    if (this.labels !== null) {
      return this.labels;
    }
    const { width, height } = this;
    const cells = this.folded.cells;
    const size = width * height;
    const labels = new Int32Array(size).fill(-1);
    const stack = new Int32Array(size);
    for (let start = 0; start < size; start += 1) {
      const value = cells[start] as number;
      if (value === 0 || (labels[start] as number) >= 0) {
        continue;
      }
      const label = this.sizes.length;
      let count = 0;
      let top = 0;
      labels[start] = label;
      stack[top++] = start;
      while (top > 0) {
        const index = stack[--top] as number;
        count += 1;
        const x = index % width;
        if (x > 0 && labels[index - 1] === -1 && cells[index - 1] === value) {
          labels[index - 1] = label;
          stack[top++] = index - 1;
        }
        if (x < width - 1 && labels[index + 1] === -1 && cells[index + 1] === value) {
          labels[index + 1] = label;
          stack[top++] = index + 1;
        }
        if (index >= width && labels[index - width] === -1 && cells[index - width] === value) {
          labels[index - width] = label;
          stack[top++] = index - width;
        }
        if (index + width < size && labels[index + width] === -1 && cells[index + width] === value) {
          labels[index + width] = label;
          stack[top++] = index + width;
        }
      }
      this.sizes.push(count);
    }
    this.labels = labels;
    return labels;
  }
}

/**
 * Whether a layer holds a field that follows a zone.
 *
 * @param layer The layer.
 * @returns True when one does.
 */
function followsAnyZone(layer: LayerDocument): boolean {
  if (layer.kind === "entity") {
    return layer.entities.some((e) => fieldsFollow(e.fields));
  }
  if (layer.kind === "prop") {
    return layer.props.some((p) => p.behaviour !== undefined && fieldsFollow(p.behaviour.fields));
  }
  return false;
}

/**
 * Whether any field, or any row of a list, follows a zone.
 *
 * @param fields The fields.
 * @returns True when one does.
 */
function fieldsFollow(fields: { readonly [key: string]: JsonValue }): boolean {
  return Object.values(fields).some((value) => cellSourceOf(value) !== null || (Array.isArray(value) && value.some((row) => cellSourceOf(row) !== null)));
}

/**
 * A thing's fields with every followed zone lowered, recording each.
 *
 * @param fields The fields.
 * @param box The thing's anchor and box.
 * @param resolver The map's resolver.
 * @param owner The thing.
 * @param followed Where each followed zone is recorded.
 * @returns The fields, the same object when none follows a zone.
 */
function lowerFields(fields: { readonly [key: string]: JsonValue }, box: ThingBox, resolver: ZoneResolver, owner: ReferenceThing, followed: FollowedZone[]): { readonly [key: string]: JsonValue } {
  if (!fieldsFollow(fields)) {
    return fields;
  }
  const out: { [key: string]: JsonValue } = {};
  for (const [name, value] of Object.entries(fields)) {
    const zone = cellSourceOf(value);
    if (zone !== null) {
      const { pairs, clipped } = resolver.resolve(zone, box);
      followed.push({ owner, address: { field: name }, zone, cells: pairs.length, clipped });
      if (pairs.length > 0) {
        out[name] = pairs;
      }
      continue;
    }
    if (Array.isArray(value) && value.some((row) => cellSourceOf(row) !== null)) {
      out[name] = value.map((row, index) => {
        const rowZone = cellSourceOf(row);
        if (rowZone === null) {
          return row;
        }
        const { pairs, clipped } = resolver.resolve(rowZone, box);
        followed.push({ owner, address: { field: name, index }, zone: rowZone, cells: pairs.length, clipped });
        return pairs;
      });
      continue;
    }
    out[name] = value;
  }
  return out;
}

/**
 * A thing named as the Issues list names it: "marker m-3 is a Creature spawn" or "prop p-2 is a Trap".
 *
 * @param map The map.
 * @param owner The thing.
 * @param types The registered types.
 * @returns The words.
 */
function thingWords(map: MapDocument, owner: ReferenceThing, types: ReferenceTypes): string {
  const type = typeOf(map, owner);
  if (owner.kind === "prop") {
    return `prop ${owner.propId} is a ${types.objectTypes.find((t) => t.type === type)?.label ?? type}`;
  }
  return `marker ${owner.entityId} is a ${types.entityTypes.find((t) => t.type === type)?.label ?? type}`;
}

/**
 * The type of a thing: a prop's behaviour type or a marker's type.
 *
 * @param map The map.
 * @param owner The thing.
 * @returns The type, or an empty string when the thing is gone.
 */
function typeOf(map: MapDocument, owner: ReferenceThing): string {
  const layer = map.layers.find((l) => l.id === owner.layerId);
  if (owner.kind === "prop" && layer?.kind === "prop") {
    return layer.props.find((p) => p.id === owner.propId)?.behaviour?.type ?? "";
  }
  if (owner.kind === "marker" && layer?.kind === "entity") {
    return layer.entities.find((e) => e.id === owner.entityId)?.type ?? "";
  }
  return "";
}

/**
 * A field's title as the inspector shows it, a list row's with its number ("Ring 2").
 *
 * @param map The map.
 * @param owner The thing.
 * @param address The field, or one row of it.
 * @param types The registered types.
 * @returns The title, else the field's name.
 */
function fieldTitle(map: MapDocument, owner: ReferenceThing, address: FieldAddress, types: ReferenceTypes): string {
  const type = typeOf(map, owner);
  const schema: JsonSchema | undefined = owner.kind === "prop" ? types.objectTypes.find((t) => t.type === type)?.schema : types.entityTypes.find((t) => t.type === type)?.schema;
  const title = schema?.properties[address.field]?.title ?? address.field;
  return address.index === undefined ? title : `${title} ${address.index + 1}`;
}
